import type { AgentSkill } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import {
    AgentSkillCategory,
    AgentSkillKind,
    AgentSkillLoadMode,
} from "@common/types/agentSkill";
import { Link } from "@design-system/Link/Link";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import type { AgentSkillMetadataPatch } from "./AgentSkillSidePanel";
import {
    AGENT_SKILL_CATEGORY_LABELS,
    AGENT_SKILL_CONSUMER_LABELS,
    AGENT_SKILL_LOAD_MODE_DESCRIPTIONS,
    AGENT_SKILL_LOAD_MODE_LABELS,
    CUSTOMIZED_LABEL,
    FIRM_SCOPE_LABEL,
    KICK_DEFAULT_LABEL,
    METADATA_LOCKED_HINT,
    SUMMARY_PENDING_MESSAGE,
} from "./agentSkillCopy";
import styles from "./AgentSkills.module.scss";

interface Props {
    skill: AgentSkill;
    clientName: string | null;
    /** Firm skills only: category and frequency save on pick. */
    canEditMetadata: boolean;
    /** A pick appends a version, which would replace the unsaved text. */
    hasUnsavedChanges: boolean;
    /** The pick in flight: shown in place of the saved value, pickers locked. */
    pendingMetadata: AgentSkillMetadataPatch | null;
    onMetadataChange: (patch: AgentSkillMetadataPatch) => void;
}

const CATEGORY_ITEMS: Array<PickerItemData<AgentSkillCategory>> = Object.values(
    AgentSkillCategory,
).map((value) => ({ value, label: AGENT_SKILL_CATEGORY_LABELS[value] }));

const LOAD_MODE_ITEMS: Array<PickerItemData<AgentSkillLoadMode>> =
    Object.values(AgentSkillLoadMode).map((value) => ({
        value,
        label: AGENT_SKILL_LOAD_MODE_LABELS[value],
        description: AGENT_SKILL_LOAD_MODE_DESCRIPTIONS[value],
    }));

const sourceLabel = (skill: AgentSkill): string => {
    if (skill.isDefault) {
        return KICK_DEFAULT_LABEL;
    }
    if (skill.isCustomized) {
        return CUSTOMIZED_LABEL;
    }
    return "Firm";
};

const DetailsRow = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className={styles.detailsRow}>
        <span className="text-secondary">{label}</span>
        {children}
    </div>
);

const TagList = ({ items }: { items: string[] }) => (
    <HStack gap={4} wrap>
        {items.map((item) => (
            <Tag key={item} color="neutral" size="sm" outline>
                {item}
            </Tag>
        ))}
    </HStack>
);

export const AgentSkillDetailsTab = ({
    skill,
    clientName,
    canEditMetadata,
    hasUnsavedChanges,
    pendingMetadata,
    onMetadataChange,
}: Props) => {
    const isKick = skill.kind === AgentSkillKind.KICK;
    const hasMetadataPickers = !isKick && canEditMetadata;
    const isPickerLocked = pendingMetadata !== null || hasUnsavedChanges;
    const { summary } = skill;
    const consumerLabels = (summary?.consumers ?? []).flatMap((consumer) => {
        const label = AGENT_SKILL_CONSUMER_LABELS[consumer];
        return label === undefined ? [] : [label];
    });
    return (
        <VStack gap={8}>
            <DetailsRow label="Source">
                <Tag color="neutral" size="sm" outline>
                    {sourceLabel(skill)}
                </Tag>
            </DetailsRow>
            <DetailsRow label="Scope">
                <Tag color="neutral" size="sm" outline>
                    {clientName ?? FIRM_SCOPE_LABEL}
                </Tag>
            </DetailsRow>
            <DetailsRow label="State">
                <Tag color="neutral" size="sm">
                    {skill.isEnabled ? "Enabled" : "Disabled"}
                </Tag>
            </DetailsRow>
            <DetailsRow label="Version">
                <span>{skill.currentVersion}</span>
            </DetailsRow>
            <DetailsRow label="Title">
                <span>{skill.title}</span>
            </DetailsRow>
            <DetailsRow label="Description">
                <span>{skill.description}</span>
            </DetailsRow>
            <DetailsRow label="Category">
                {hasMetadataPickers ? (
                    <Picker.Select
                        items={CATEGORY_ITEMS}
                        value={pendingMetadata?.category ?? skill.category}
                        disabled={isPickerLocked}
                        onValueChange={(category) => {
                            if (category && category !== skill.category) {
                                onMetadataChange({ category });
                            }
                        }}
                    />
                ) : (
                    <span>{AGENT_SKILL_CATEGORY_LABELS[skill.category]}</span>
                )}
            </DetailsRow>
            <DetailsRow label="Frequency">
                {hasMetadataPickers ? (
                    <Picker.Select
                        items={LOAD_MODE_ITEMS}
                        value={pendingMetadata?.loadMode ?? skill.loadMode}
                        disabled={isPickerLocked}
                        onValueChange={(loadMode) => {
                            if (loadMode && loadMode !== skill.loadMode) {
                                onMetadataChange({ loadMode });
                            }
                        }}
                    />
                ) : (
                    <span>{AGENT_SKILL_LOAD_MODE_LABELS[skill.loadMode]}</span>
                )}
            </DetailsRow>
            {hasMetadataPickers && hasUnsavedChanges && (
                <span className="text-secondary">{METADATA_LOCKED_HINT}</span>
            )}
            {isKick && (
                <DetailsRow label="Tools">
                    <TagList items={skill.tools} />
                </DetailsRow>
            )}
            <h3 className={styles.detailsHeading}>Summary</h3>
            {summary === null ? (
                <span className="text-secondary">
                    {SUMMARY_PENDING_MESSAGE}
                </span>
            ) : (
                <>
                    <DetailsRow label="Purpose">
                        <span>{summary.purpose}</span>
                    </DetailsRow>
                    {summary.usage !== "" && (
                        <DetailsRow label="When to use">
                            <span>{summary.usage}</span>
                        </DetailsRow>
                    )}
                    <DetailsRow label="Tools">
                        <TagList items={summary.tools} />
                    </DetailsRow>
                    {summary.relatedSkills.length > 0 && (
                        <DetailsRow label="Related skills">
                            <HStack gap={8} wrap>
                                {summary.relatedSkills.map((name) => (
                                    <Link
                                        key={name}
                                        render={
                                            <StateAwareLink
                                                to={`/automation/skills?search=${encodeURIComponent(name.replace(/^kick\//, ""))}`}
                                            />
                                        }
                                    >
                                        {name}
                                    </Link>
                                ))}
                            </HStack>
                        </DetailsRow>
                    )}
                    {consumerLabels.length > 0 && (
                        <DetailsRow label="Used by">
                            <TagList items={consumerLabels} />
                        </DetailsRow>
                    )}
                </>
            )}
        </VStack>
    );
};
